import React from 'react'
import styled from 'styled-components'
import type { TFunction } from 'i18next'
import type { Building } from '@popety-io/types'
import { Icon } from '../../../atoms/Icon'
import {
  Card,
  CardHeader,
  CardTitle,
  MetricGrid,
  MetricRow,
  MetricLabel,
  MetricValue,
} from '../EnergyTab.styles'

interface SolarInstallationSectionProps {
  building: Building
  t: TFunction
  compact?: boolean
  className?: string
}

const NoDataCard = styled.div`
  padding: 32px;
  text-align: center;
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  border: 1px dashed ${({ theme }) => theme.colors.border.light};
  color: ${({ theme }) => theme.colors.text.secondary};
`

const InstallationTimeline = styled.div`
  padding: 0 20px 20px;
`

const TimelineHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 16px;
`

const TimelineList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const TimelineItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
`

const TimelineDate = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  min-width: 80px;
`

const TimelinePower = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`

const TotalCapacity = styled.div`
  margin-top: 16px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #f59e0b15 0%, #f59e0b08 100%);
  border: 1px solid #f59e0b30;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const TotalLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`

const TotalValue = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: #f59e0b;
`

export const SolarInstallationSection: React.FC<
  SolarInstallationSectionProps
> = ({ building, t, compact = false, className }) => {
  const {
    solar_installed_power,
    solar_installed_first_date,
    solar_installed_date,
    photovoltaic_history,
  } = building

  const hasSolarInstallation =
    solar_installed_power && solar_installed_power > 0
  const hasHistory = photovoltaic_history && photovoltaic_history.length > 0

  // Sort installation history by date (oldest first)
  const sortedHistory = hasHistory
    ? [...photovoltaic_history].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      )
    : []

  // Calculate total capacity from history
  const totalFromHistory = sortedHistory.reduce(
    (sum, entry) => sum + (entry.power || 0),
    0
  )

  if (!hasSolarInstallation && !hasHistory) {
    return (
      <NoDataCard className={className}>
        <Icon
          name="sun"
          size="large"
          style={{ opacity: 0.3, marginBottom: '12px' }}
        />
        <p>{t('solarInstallation.noData')}</p>
      </NoDataCard>
    )
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>
          <Icon name="sun" size="small" color="#f59e0b" />
          {t('solarInstallation.title')}
        </CardTitle>
      </CardHeader>

      <MetricGrid>
        <MetricRow>
          <MetricLabel>
            <Icon name="zap" size="small" />
            {t('solarInstallation.totalInstalledCapacity')}
          </MetricLabel>
          <MetricValue>
            {solar_installed_power || totalFromHistory.toFixed(2)} kWp
          </MetricValue>
        </MetricRow>

        {solar_installed_first_date && (
          <MetricRow>
            <MetricLabel>
              <Icon name="calendar" size="small" />
              {t('solarInstallation.firstInstallation')}
            </MetricLabel>
            <MetricValue>
              {new Date(solar_installed_first_date).toLocaleDateString(
                'en-US',
                {
                  month: 'short',
                  year: 'numeric',
                }
              )}
            </MetricValue>
          </MetricRow>
        )}

        {solar_installed_date && !hasHistory && (
          <MetricRow>
            <MetricLabel>
              <Icon name="calendar" size="small" />
              {t('solarInstallation.installationYear')}
            </MetricLabel>
            <MetricValue>{solar_installed_date}</MetricValue>
          </MetricRow>
        )}
      </MetricGrid>

      {/* Installation History Timeline */}
      {hasHistory && !compact && (
        <InstallationTimeline>
          <TimelineHeader>
            <Icon name="clock" size="small" />
            {t('solarInstallation.installationHistory')}
          </TimelineHeader>
          <TimelineList>
            {sortedHistory.map((entry, index) => (
              <TimelineItem key={index}>
                <TimelineDate>
                  {new Date(entry.date).toLocaleDateString('en-US', {
                    month: 'short',
                    year: 'numeric',
                  })}
                </TimelineDate>
                <TimelinePower>
                  {entry.power} kWp{' '}
                  {index === 0
                    ? t('solarInstallation.installed')
                    : t('solarInstallation.added')}
                </TimelinePower>
              </TimelineItem>
            ))}
          </TimelineList>
          {sortedHistory.length > 1 && (
            <TotalCapacity>
              <TotalLabel>
                {t('solarInstallation.totalInstalledCapacity')}
              </TotalLabel>
              <TotalValue>{totalFromHistory.toFixed(2)} kWp</TotalValue>
            </TotalCapacity>
          )}
        </InstallationTimeline>
      )}

      {/* Compact mode: show summary of installation history */}
      {hasHistory && compact && (
        <MetricGrid>
          <MetricRow>
            <MetricLabel>
              <Icon name="clock" size="small" />
              {t('solarInstallation.installationEvents')}
            </MetricLabel>
            <MetricValue>
              {sortedHistory.length > 1
                ? t('solarInstallation.installationsCount', {
                    count: sortedHistory.length,
                  })
                : t('solarInstallation.installationCount', {
                    count: sortedHistory.length,
                  })}
            </MetricValue>
          </MetricRow>
        </MetricGrid>
      )}
    </Card>
  )
}
